import { EyeIcon, PencilIcon, PhotographIcon, TrashIcon, XIcon } from "@heroicons/react/outline";
import ImageUploading from "react-images-uploading";
import { useState } from "react";
import Modal from "react-modal";
import { v4 as uuidv4 } from "uuid";
import { gql, useApolloClient } from "@apollo/client";
import axios from "axios";
import PropsType from "prop-types";
import User from "@gcm/libs/user";

Modal.setAppElement("#root");

function Hero({ business, image, aspect }) {
  const [images, setImages] = useState(image ? [`${CONFIG.GOOGLE_STORAGE_PUBLICURL}/${image}`] : []);
  const [imageModal, setImageModal] = useState({ open: false, image: null });
  const apolloClient = useApolloClient();
  const user = User();
  const canEdit = user.isInRole(["ADMIN"]);

  async function onChange(imageList, addUpdateIndex) {
    const { data_url, file } = imageList[addUpdateIndex];

    const extension = file.name.split(".").pop();
    const filename = `business/${business}/${uuidv4()}.${extension}`;

    const signedUrlResponse = await apolloClient.query({
      query: gql`
        query GetSignedUrl($filename: String, $type: String) {
          getSignedUrl(filename: $filename, type: $type)
        }
      `,
      variables: {
        filename,
        type: file.type,
      },
    });

    const signedUrl = signedUrlResponse.data.getSignedUrl;
    const imageBlob = await (await fetch(data_url)).blob();

    try {
      const uploadImageResponse = await axios.put(signedUrl, imageBlob, {
        headers: {
          "Content-Type": file.type,
          "Access-Control-Allow-Origin": "*",
        },
      });

      const saveImageResponse = await apolloClient.mutate({
        mutation: gql`
          mutation SaveBusinessHeroImage($id: String!, $path: String!, $aspect: String!) {
            saveBusinessHeroImage(id: $id, path: $path, aspect: $aspect) {
              id
              hero
              heroSquare
            }
          }
        `,
        variables: {
          path: filename,
          id: business,
          aspect,
        },
        refetchQueries: ["GetBusiness"],
        onQueryUpdated(observableQuery) {
          return true;
        }
      });

      setImages([imageList[0].data_url]);
    } catch (err) {
      console.log(err);
    }
  }

  async function deleteHero() {
    const deleteResponse = await apolloClient.mutate({
      mutation: gql`
        mutation DeleteBusinessHeroImage($id: String!, $aspect: String!) {
          deleteBusinessHeroImage(id: $id) {
            id
            hero
            heroSquare
          }
        }
      `,
      variables: {
        id: business,
        aspect,
      },
      refetchQueries: ["GetBusiness"],
      onQueryUpdated(observableQuery) {
        return true;
      }
    });

    setImages([]);
    setImageModal({ open: false, image: null });
  }

  return (
    <>
      <div>
        <ImageUploading value={images} maxNumber={1} onChange={onChange} dataURLKey="data_url">
          {({
            imageList,
            onImageUpload,
            onImageUpdate,
          }) => (
            <div className="upload__image-wrapper w-24 h-24 flex items-center">
              {images.length === 0 && (
                <button
                  className="transparent p-0 w-full h-full"
                  onClick={() => {
                    if (canEdit) onImageUpload();
                    else return;
                  }}>
                  <div className="bg-gray rounded w-full h-full flex items-center justify-center">
                    <PhotographIcon className="w-6 h-6" />
                  </div>
                </button>
              )}
              {
                images.length > 0 && (
                  <div className="relative w-full h-full flex justify-center">
                    <div className="flex items-center justify-center">
                      <img src={images[0]} className="rounded" />
                    </div>
                    <div className="absolute bottom-1 w-full">
                      <div className="bg-white rounded-full shadow flex p-2 justify-center mx-2">
                        {canEdit && (
                          <>
                            <button className="transparent p-0" onClick={onImageUpdate}>
                              <PencilIcon className="w-3 h-3" />
                            </button>
                            <div className=" mx-2 bg-gray" style={{ width: "1px" }} />
                          </>
                        )}
                        <button
                          className="transparent p-0"
                          onClick={() => {
                            setImageModal({ open: true, image: images[0] });
                          }}
                        >
                          <EyeIcon className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              }
            </div>
          )
          }
        </ImageUploading>
        <div className="text-center font-bold text-xs py-2">Hero{`${aspect === "square" ? " (Square)" : ""}`}</div>
      </div>

      <Modal
        isOpen={imageModal.open}
        style={{
          overlay: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#00000030",
          },
          content: {
            inset: "unset",
            width: "40%",
            display: "flex",
            flexDirection: "column",
            padding: 0,
            backgroundColor: "transparent",
            border: 0,
            borderRadius: 0,
          }
        }}
      >
        <>
          {imageModal.image && (
            <div className="flex flex-1 items-center justify-center">
              <img
                style={{ maxHeight: "70vh" }}
                src={imageModal.image}
                alt=""
              />
            </div>
          )}
          <div className="bg-transparent flex mt-4 justify-center">
            <div className="bg-white rounded">
              <button
                className="mx-1 transparent"
                onClick={() => {
                  setImageModal({ open: false, image: null });
                }}
              >
                <XIcon className="w-6 h-6" />
              </button>
              {canEdit && (
                <button
                  className="mx-1 transparent"
                  onClick={deleteHero}
                >
                  <TrashIcon className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>
        </>
      </Modal>
    </>
  )
}

export default Hero;

Hero.propTypes = {
  aspect: PropsType.oneOf(["square", "landscape"]),
}
